import { Box, Center, Text, VStack } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useIsFetching } from 'react-query';
import { useTheme } from '../../../../../packages/ui/contexts/ThemeProvider';
import { keyframes } from '@emotion/react';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const bookPulse = keyframes`
  0%, 100% { transform: translateY(0) scale(1) rotate(0deg); }
  25% { transform: translateY(-4px) scale(1.03) rotate(-1.5deg); }
  50% { transform: translateY(-10px) scale(1.07) rotate(0deg); }
  75% { transform: translateY(-4px) scale(1.03) rotate(1.5deg); }
`;

const breath = keyframes`
  0%, 100% { transform: scale(0.85); opacity: 0.45; }
  50% { transform: scale(1.15); opacity: 0.85; }
`;

const halo = keyframes`
  0% { transform: scale(0.5); opacity: 0.7; }
  60% { opacity: 0.2; }
  100% { transform: scale(2.2); opacity: 0; }
`;

const flipPage = keyframes`
  0% { transform: rotateY(0deg); }
  20% { transform: rotateY(-180deg); }
  100% { transform: rotateY(-180deg); }
`;

const textPulse = keyframes`
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.04); }
`;

const MIN_DURATION_MS = 900;
const HIDE_DELAY_MS = 250;
const FADE_OUT_MS = 400;

function hexToRgba(hex: string, alpha: number) {
  const cleaned = hex.replace('#', '');
  if (cleaned.length !== 6) return hex;
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function BookLoadingOverlay({
  description = 'Carregando sua biblioteca...',
}: {
  description?: string;
}) {
  const { isNative, colors } = useTheme();
  const isFetching = useIsFetching();
  const startedAtRef = useRef(Date.now());
  const [hiding, setHiding] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!isNative) {
      setHidden(true);
      return;
    }
    if (hidden) return;

    if (isFetching > 0) {
      setHiding(false);
      return;
    }

    const elapsed = Date.now() - startedAtRef.current;
    const wait = Math.max(HIDE_DELAY_MS, MIN_DURATION_MS - elapsed);

    let finalizeTimeout: ReturnType<typeof setTimeout> | undefined;
    const startHideTimeout = setTimeout(() => {
      setHiding(true);
      finalizeTimeout = setTimeout(() => setHidden(true), FADE_OUT_MS);
    }, wait);

    return () => {
      clearTimeout(startHideTimeout);
      if (finalizeTimeout) clearTimeout(finalizeTimeout);
    };
  }, [isFetching, isNative, hidden]);

  if (!isNative || hidden) return null;

  const primary = colors?.primaryColor || '#3B82F6';

  return (
    <Center
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={9999}
      bg={hexToRgba(primary, 0.1)}
      backdropFilter="blur(10px)"
      overflow="hidden"
      animation={`${hiding ? fadeOut : fadeIn} ${FADE_OUT_MS}ms ease-in-out forwards`}
      _before={{
        content: '""',
        position: 'absolute',
        inset: 0,
        background:
          'radial-gradient(ellipse at center, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 60%)',
        pointerEvents: 'none',
      }}
    >
      <VStack spacing={12} position="relative" zIndex={1}>
        <Box
          position="relative"
          width="220px"
          height="220px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box
            position="absolute"
            top="50%"
            left="50%"
            width="200px"
            height="200px"
            ml="-100px"
            mt="-100px"
            borderRadius="50%"
            background={`radial-gradient(circle, ${hexToRgba(primary, 0.55)} 0%, ${hexToRgba(primary, 0)} 70%)`}
            filter="blur(6px)"
            animation={`${breath} 2.6s ease-in-out infinite`}
          />

          {[0, 1, 2, 3].map((i) => (
            <Box
              key={i}
              position="absolute"
              top="50%"
              left="50%"
              width="120px"
              height="120px"
              ml="-60px"
              mt="-60px"
              borderRadius="50%"
              border="2px solid"
              borderColor={hexToRgba(primary, 0.55)}
              animation={`${halo} 2.6s ${i * 0.65}s ease-out infinite`}
            />
          ))}

          <Box
            width="84px"
            height="68px"
            position="relative"
            sx={{ perspective: '700px' }}
            animation={`${bookPulse} 2.4s ease-in-out infinite`}
            filter={`drop-shadow(0 8px 14px ${hexToRgba(primary, 0.4)}) drop-shadow(0 2px 4px rgba(0,0,0,0.15))`}
          >
            <Box
              position="absolute"
              left="50%"
              top="100%"
              mt="12px"
              transform="translateX(-50%)"
              width="64px"
              height="6px"
              borderRadius="50%"
              bg="rgba(0,0,0,0.3)"
              filter="blur(4px)"
            />
            <Box
              position="absolute"
              left={0}
              top={0}
              bottom={0}
              width="50%"
              bg="white"
              borderLeftRadius="5px"
              boxShadow="inset -3px 0 8px rgba(0,0,0,0.12)"
            />
            <Box
              position="absolute"
              right={0}
              top={0}
              bottom={0}
              width="50%"
              bg="white"
              borderRightRadius="5px"
              boxShadow="inset 3px 0 8px rgba(0,0,0,0.12)"
            />
            {[0, 1, 2, 3].map((i) => (
              <Box
                key={i}
                position="absolute"
                left="50%"
                top="7%"
                height="86%"
                width="46%"
                bg="white"
                borderRightRadius="2px"
                boxShadow="0 1px 3px rgba(0,0,0,0.12)"
                transformOrigin="left center"
                animation={`${flipPage} 2.4s ${i * 0.25}s ease-in-out infinite`}
                sx={{ backfaceVisibility: 'hidden' }}
              />
            ))}
          </Box>
        </Box>

        <Text
          fontSize="md"
          fontWeight="600"
          color={colors?.primaryColor || '#1A202C'}
          textAlign="center"
          letterSpacing="0.3px"
          textShadow="0 1px 2px rgba(0,0,0,0.15)"
          animation={`${textPulse} 1.8s ease-in-out infinite`}
        >
          {description}
        </Text>
      </VStack>
    </Center>
  );
}
